/**
 * Application role codes — the single source of truth for the portal's own
 * roles. Add, rename or remove a role here and every consumer (access checks,
 * user forms, role-gated UI) follows automatically.
 *
 * Note: Keycloak additionally issues default roles to every account
 * (offline_access, uma_authorization, default-roles-*). Those are NOT
 * application roles and are intentionally excluded from this list.
 */
export const APP_ROLES = {
  ADMIN: 'adm',
  OFFICE_ADMIN: 'ofa',
  CAREGIVER: 'cgv',
  CLIENT: 'clt',
  DOCTOR: 'doc',
} as const;

/** A recognized application role code (e.g. `'adm'`). */
export type AppRole = (typeof APP_ROLES)[keyof typeof APP_ROLES];

/** All recognized application role codes. */
export const APP_ROLE_CODES = Object.values(APP_ROLES) as AppRole[];

/**
 * Roles that grant access to the staff portal.
 *
 * The doctor sits here with the two admin roles because the doctor's screens are
 * this same build, role-gated — not a separate app.
 *
 * The ORDER matters: it is also the order `portalPersona` reads, so the staff
 * portal wins over the doctor's for an account holding both.
 */
export const PORTAL_ROLES: readonly AppRole[] = [APP_ROLES.ADMIN, APP_ROLES.OFFICE_ADMIN, APP_ROLES.DOCTOR];

/**
 * True if the given roles may enter the staff portal at all.
 *
 * Holding a portal role is the whole test. A caregiver or a client is refused not
 * because of the role they hold but because of the one they lack, which is why an
 * account that is both a caregiver and a doctor is admitted — the same way one
 * that is both a caregiver and an administrator always has been.
 */
export const canAccessPortal = (roles: readonly string[]): boolean =>
  roles.some((role) => (PORTAL_ROLES as readonly string[]).includes(role));

/**
 * Which portal this account is shown, of the ones its roles open.
 *
 * A dashboard question, not a backend one. The server picks the role a request
 * runs as from the path it was sent to — anything under `/doctor/...` acts as a
 * doctor — so one account can be both, and nothing here has to agree with a
 * server-side ordering. What is left to decide is which set of screens to open,
 * and the first role in `PORTAL_ROLES` that the account holds wins.
 */
export const portalPersona = (roles: readonly string[]): AppRole | null =>
  PORTAL_ROLES.find((role) => roles.includes(role)) ?? null;

/**
 * True if this account is shown the doctor portal.
 *
 * Not `roles.includes('doc')`. `restrictToDoctorPortal` hides every staff screen,
 * so an administrator who is also a doctor must not answer to this — they keep
 * the staff portal and reach the doctor's endpoints only if a doctor screen ever
 * calls them.
 */
export const isDoctorPersona = (roles: readonly string[]): boolean => portalPersona(roles) === APP_ROLES.DOCTOR;

/** True if the given list contains at least one recognized application role. */
export const hasAnyAppRole = (roles: readonly string[]): boolean =>
  roles.some((role) => (APP_ROLE_CODES as readonly string[]).includes(role));

/**
 * The role only a true administrator may hand out — or take away.
 *
 * Mirrors ADMIN_ONLY_ROLE in the user-service router. `ofa` is deliberately NOT
 * here: office administrators manage ordinary staff, other office administrators
 * included. Only an administrator is beyond them, in either direction.
 *
 * This is presentation only. The backend is the control — it refuses the change
 * whatever the form offers, and a caller can post whatever they like. What this
 * prevents is a UI inviting an office administrator to make a change the server
 * will reject.
 */
export const ADMIN_ONLY_ROLE: AppRole = APP_ROLES.ADMIN;

/**
 * The roles a form may offer, given whether the signed-in user is an administrator.
 *
 * `alreadyHeld` is what the account being edited holds NOW. An administrator's own
 * `adm` stays in the list for a non-admin editor, because dropping it from the
 * options would submit a payload without it — and `roles` is replace, not merge,
 * so that silently demotes them. Offering it, already ticked, is how an office
 * administrator edits that user's OTHER roles without touching this one.
 */
export const grantableRoles = <T extends { code: string }>(
  roles: readonly T[],
  isAdmin: boolean,
  alreadyHeld: readonly string[] = []
): T[] =>
  isAdmin ? [...roles] : roles.filter((role) => role.code !== ADMIN_ONLY_ROLE || alreadyHeld.includes(ADMIN_ONLY_ROLE));
